/**
 * CRM Multi-Compagnia · Hub Migrazione SIA Omnia 8
 *
 * 3 card per le modalità di import (A attiva, B/C placeholder "In arrivo")
 * + lista batch recenti.
 */
import { useState } from "react";
import { Upload, Cloud, Database, ChevronRight, BookOpen, RefreshCw } from "lucide-react";
import {
  api, useApi, Spinner, ErrorBox, Btn, fmtDateTime, StatusBadge
} from "@/components/crm-multi/shared";

const MODI = [
  { id: "A", icon: Upload, title: "Modalità A — Import file manuali", active: true,
    descr: "Carica CSV / Excel / XML esportati manualmente da SIA Omnia. Wizard guidato a 4 step." },
  { id: "B", icon: Cloud, title: "Modalità B — API SIA", active: false,
    descr: "Importazione automatica via API. Richiede credenziali SIA del cliente." },
  { id: "C", icon: Database, title: "Modalità C — Dump database", active: false,
    descr: "Importazione massiva da dump SQL o backup completo del DB SIA." },
];

export default function SiaHubPage({ onNavigate }) {
  const batches = useApi(() => api.get("/sia/batches", { limit: 10 }));

  return (
    <div className="space-y-6" data-testid="sia-hub-page">
      <header className="flex justify-between items-end flex-wrap gap-3">
        <div>
          <h2 className="text-xl font-semibold text-zinc-900">📥 Migrazione SIA Omnia 8</h2>
          <p className="text-sm text-zinc-500">Tre modalità di importazione dati storici dal vecchio gestionale</p>
        </div>
        <Btn variant="outline" onClick={() => onNavigate?.("sia_checklist")} data-testid="sia-hub-checklist">
          <BookOpen className="w-4 h-4" /> Checklist Export SIA
        </Btn>
      </header>

      <div className="grid md:grid-cols-3 gap-4">
        {MODI.map(m => {
          const Icon = m.icon;
          const cls = m.active ? "border-emerald-300 hover:border-emerald-500 hover:shadow-lg cursor-pointer bg-white" : "border-zinc-200 opacity-70 bg-zinc-50 cursor-not-allowed";
          return (
            <button key={m.id} onClick={() => m.active && onNavigate?.("sia_import")}
              disabled={!m.active}
              data-testid={`sia-mode-${m.id}`}
              className={`p-5 rounded-xl border-2 text-left transition ${cls}`}>
              <div className="flex items-start justify-between mb-3">
                <div className={`w-10 h-10 rounded-lg ${m.active ? "bg-emerald-50" : "bg-zinc-100"} flex items-center justify-center`}>
                  <Icon className={`w-5 h-5 ${m.active ? "text-emerald-600" : "text-zinc-400"}`} />
                </div>
                {!m.active && <span className="px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 text-[10px] font-medium uppercase tracking-wider">In arrivo</span>}
              </div>
              <h3 className="font-semibold text-sm text-zinc-900 mb-1">{m.title}</h3>
              <p className="text-xs text-zinc-500 leading-relaxed">{m.descr}</p>
              {m.active && (
                <div className="mt-3 text-emerald-700 text-xs font-medium inline-flex items-center gap-1">
                  Avvia wizard <ChevronRight className="w-3 h-3" />
                </div>
              )}
            </button>
          );
        })}
      </div>

      <section>
        <div className="flex items-center justify-between mb-2">
          <h3 className="font-semibold text-sm text-zinc-900">Batch recenti</h3>
          <Btn variant="ghost" size="sm" onClick={() => batches.refresh()}><RefreshCw className="w-3.5 h-3.5" /> Aggiorna</Btn>
        </div>
        <ErrorBox error={batches.error} />
        {batches.loading ? <Spinner /> : (batches.data?.items || []).length === 0 ? (
          <div className="text-center py-12 border-2 border-dashed border-zinc-200 rounded-xl">
            <p className="text-sm text-zinc-500">Nessun batch ancora — clicca Modalità A per importare il primo file</p>
          </div>
        ) : (
          <div className="bg-white border rounded-lg overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-zinc-50 text-xs uppercase text-zinc-500">
                <tr>
                  <th className="text-left px-3 py-2">File</th>
                  <th className="text-left">Tipo</th>
                  <th className="text-left">Righe</th>
                  <th className="text-left">Stato</th>
                  <th className="text-left">Quando</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {(batches.data?.items || []).map(b => (
                  <tr key={b.id} className="border-t border-zinc-100 hover:bg-emerald-50/40">
                    <td className="px-3 py-2 text-xs font-mono">{b.nome_file || "—"}</td>
                    <td className="text-xs capitalize">{b.tipo_import}</td>
                    <td className="text-xs">{b.righe_totali}</td>
                    <td><span className={`text-xs px-2 py-0.5 rounded ${b.stato === "completed" ? "bg-emerald-100 text-emerald-800" : b.stato === "fallito" ? "bg-rose-100 text-rose-800" : "bg-amber-100 text-amber-800"}`}>{b.stato}</span></td>
                    <td className="text-xs">{fmtDateTime(b.started_at)}</td>
                    <td className="px-3 py-2 text-right">
                      <Btn size="sm" variant="ghost" onClick={() => window.open(`${process.env.REACT_APP_BACKEND_URL}/api/admin/crm/multi/sia/batches/${b.id}/report.pdf`, "_blank")}>PDF</Btn>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}
